import React, { useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { useForm, FieldErrors } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import { checkListQuestionFormValues } from "../helpers/checklist.types";
import { fields } from "../helpers/checklistconstant";

/* -----------------------------
 Types
----------------------------- */

interface checkListAddEditModalProps {
  isOpen: boolean;
  toggle: () => void;
  existingData: any | null;
  preSelectedType?: any | null;
  onSave: (record: any) => void;
  isLoading: boolean;
}

/* -----------------------------
 Component
----------------------------- */

const AddEditCheckListModal: React.FC<checkListAddEditModalProps> = ({
  isOpen,
  toggle,
  existingData,
  preSelectedType,
  onSave,
  isLoading,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<checkListQuestionFormValues>({
    defaultValues: {
      question: "",
      isrequired: "true",
      checklisttype: "",
    },
    mode: "onChange",
  });

  /* -----------------------------
   Populate data in edit mode
  ----------------------------- */
  useEffect(() => {
    if (existingData) {
      reset({
        question: existingData.question || "",
        isrequired: existingData.isrequired ? "true" : "false",
        checklisttype: existingData.checklisttype || (preSelectedType ? preSelectedType.value : ""),
      });
    } else {
      reset({
        question: "",
        isrequired: "true",
        checklisttype: preSelectedType ? preSelectedType.value : "",
      });
    }
  }, [existingData, preSelectedType, reset, isOpen]);



  /* -----------------------------
   Submit
  ----------------------------- */
  const onSubmit = (values: checkListQuestionFormValues): void => {
    const typeVal = typeof values.checklisttype === "object"
      ? values.checklisttype.value
      : (values.checklisttype || (preSelectedType ? preSelectedType.value : undefined));

    const payload = {
      ...(existingData || {}),
      question: values.question,
      isrequired: values.isrequired === "true",
      checklisttype: typeVal,
    };

    onSave(payload);
  };

  const dynamicFields = React.useMemo(() => {
    return fields.map((field) => {
      if (field.name === "checklisttype" && preSelectedType) {
        return { ...field, disabled: true };
      }
      return field;
    });
  }, [preSelectedType]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  /* -----------------------------
   Render
  ----------------------------- */
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
      zIndex={1070}
    >
      <ModalHeader toggle={toggle} className="p-3">
        {existingData ? "Edit Checklist" : "Add Checklist"}
      </ModalHeader>

      <ModalBody>
        <form id="icfForm" onSubmit={handleFormSubmit}>
          <CommonForm
            fields={dynamicFields}
            control={control}
            errors={errors as FieldErrors}
            columns={1}
          />
        </form>
      </ModalBody>

      <ModalFooter>
        <Button outline color="dark" size="sm" onClick={toggle} className="me-2">
          Cancel
        </Button>
        <Button
          color="primary"
          size="sm"
          type="submit"
          form="icfForm"
          disabled={isSubmitting || isLoading}
          outline
        >
          {!existingData && isLoading && <Spinner size="sm" className="me-2" />}
          {existingData ? "Update" : "Save"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddEditCheckListModal;
